import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { inspectorPool, poolLabel, type PoolEntry } from '../../../lib/inspectorPool.mjs';   // R120: one inspector list (C7)

// Ops board (Jesse: "a view where I can see the cleans and associated
// inspections all in one view") — read-only v1. A client-side join of the
// turnover board (cleans, keyed by check_out) and the inspections queue
// (keyed by checkout_date; checkin_light rows by checkin_date) on
// (listing, date), grouped by day. Lives as the first tab of the HK &
// Inspections hub; each row links across to the sibling Housekeeping /
// Inspections tabs. No mutations here — the sibling tabs own the writes.
const API = '/api/assetops';

const CLEAN_DONE = ['cleaned', 'verified', 'ready'];
const CLEAN_LABEL: Record<string, string> = { unstarted: 'Unstarted', in_progress: 'In progress', done: 'Done' };

export class OpsBoardPage {
  private container: HTMLElement;
  private days = 7;                 // horizon: 1 (today) / 3 / 7
  private board: any = null;        // /turnover-board json (null = load failed)
  private queue: any = null;        // /inspections/queue json (null = load failed)
  private cleaners: any[] = [];     // id → name for clean assignees
  // R61: quick-assign from the board (Kai). R120 (Madel: "changed her role to
  // Inspector, still not in the dropdown"): the board read ONLY the PM roster
  // (/inspections/managers), the Inspections page only logins by role — now
  // both use lib/inspectorPool: inspector logins, the inspector roster (a row
  // with a login's email shows under that login's name), the leads, and the
  // names already on queue rows. The option VALUE stays the name string.
  private inspectors: PoolEntry[] = [];
  private people: any[] | null = null;   // GET /api/tasks/assignees (null = not loaded yet)
  private roster: any[] | null = null;   // GET /inspections/managers

  constructor(container: HTMLElement) { this.container = container; }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="opb-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    await this.load();
  }

  private async load(): Promise<void> {
    const [b, q, c, m, ppl] = await Promise.all([
      fetch(`${API}/turnover-board?days=${this.days}`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/inspections/queue?days=${this.days}`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/cleaners`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/inspections/managers`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/tasks/assignees').then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.board = b;
    this.queue = q;
    if (c && c.cleaners) this.cleaners = c.cleaners;   // keep a stale roster over blanking names
    // keep the last good copy of each source over blanking the picker on a blip
    if (m && Array.isArray(m.managers)) this.roster = m.managers.map((x: any) => typeof x === 'string' ? { name: x } : x);
    if (ppl && Array.isArray(ppl.people)) this.people = ppl.people;
    this.inspectors = inspectorPool({
      profiles: this.people || [],
      roster: this.roster || [],
      jobNames: ((this.queue && this.queue.rows) || []).map((r: any) => r && r.assigned_inspector),
    });
    this.draw();
  }
  private inspectorOptions(cur: string): string {
    const opts = this.inspectors.map(p => `<option value="${this.esc(p.name)}" ${p.name === cur ? 'selected' : ''}>${this.esc(poolLabel(p))}</option>`);
    if (cur && !this.inspectors.some(p => p.name === cur)) opts.push(`<option value="${this.esc(cur)}" selected>${this.esc(cur)}</option>`);
    return opts.join('');
  }

  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  private cleanerName(id: any): string {
    const c = this.cleaners.find(x => String(x.id) === String(id));
    return c ? String(c.name) : String(id);
  }
  private asOf(): string {
    return (this.board && this.board.as_of) || (this.queue && this.queue.as_of) || new Date().toLocaleDateString('en-CA');
  }
  private fmtDay(ymd: string): string {
    const d = new Date(ymd + 'T00:00:00Z');
    return isNaN(d.getTime()) ? ymd : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  private fmtShortDay(ymd: string): string {
    const d = new Date(ymd + 'T00:00:00Z');
    return isNaN(d.getTime()) ? ymd : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  // "today" / "tomorrow" suffix relative to the server's business-tz as_of.
  private relLabel(ymd: string): string {
    const diff = Math.round((new Date(ymd + 'T00:00:00Z').getTime() - new Date(this.asOf() + 'T00:00:00Z').getTime()) / 86400000);
    return diff === 0 ? 'today' : (diff === 1 ? 'tomorrow' : '');
  }

  // The join: one row per (listing, date) with any activity — a turn, a queue
  // row, or both. checkin_light queue rows date by their arrival.
  private joined(): { date: string; rows: any[] }[] {
    const map = new Map<string, any>();
    const rowFor = (lid: any, date: string, name: any) => {
      const k = `${lid}|${date}`;
      let r = map.get(k);
      if (!r) { r = { listing_id: lid, date, name: '', turn: null, insp: null }; map.set(k, r); }
      if (name && !r.name) r.name = String(name);
      return r;
    };
    for (const t of ((this.board && this.board.turns) || [])) {
      if (!t.check_out) continue;
      rowFor(t.listing_id, t.check_out, t.listing_name).turn = t;
    }
    for (const q of ((this.queue && this.queue.rows) || [])) {
      // R81 (Jesse: "the OPS board is missing the missed inspections"): a
      // missed rollover's checkout_date is YESTERDAY — the Today filter
      // dropped it. The queue rolls it to today (roll_to); date it there.
      // R114 review: an early-walked moved job also carries roll_to — its
      // post-checkout check sits on the checkout's clean.
      const date = q.roll_to ? q.roll_to : (q.checkout_date || q.checkin_date);
      if (!date) continue;
      const r = rowFor(q.listing_id, date, q.name);
      if (!r.insp) { r.insp = q; continue; }
      // Review #6: a rolled missed row and today's REAL queue row share the
      // (listing, today) key — never let one silently erase the other. The
      // missed one gets its own synthetic row; the regular row keeps the slot.
      const missedQ = q.missed ? q : (r.insp.missed ? r.insp : null);
      const otherQ = q.missed ? r.insp : q;
      r.insp = otherQ;
      if (missedQ) {
        map.set(`${q.listing_id}|${date}|missed`, { listing_id: q.listing_id, date, name: r.name, turn: null, insp: missedQ });
      }
    }
    let rows = [...map.values()];
    // The queue emits only the SOONEST upcoming checkout per listing, so any
    // OTHER date's row for that listing has no queue row — say where the
    // inspection actually is (earlier OR later) instead of a misleading
    // '— no inspection' (R81: Jesse "I do not understand why it says no
    // inspection for some of these").
    const anyQ = new Map<string, string>();
    for (const q of ((this.queue && this.queue.rows) || [])) {
      // Same effective date as the join above — a missed row lives in TODAY.
      const d = q.roll_to ? q.roll_to : (q.checkout_date || q.checkin_date);
      if (!d) continue;
      const k = String(q.listing_id);
      if (!anyQ.has(k) || d < anyQ.get(k)!) anyQ.set(k, d);
    }
    // R114 review (insp-move #7): an inspection rescheduled AWAY from this
    // clean's date (possibly past the board's window) — say where it went
    // instead of "— no inspection" + a schedule picker the server refuses.
    const movedTo = new Map<string, string>();
    for (const m of ((this.queue && Array.isArray(this.queue.moved_away)) ? this.queue.moved_away : [])) {
      if (m && m.listing_id != null && m.origin_date && m.moved_to) movedTo.set(`${m.listing_id}|${m.origin_date}`, String(m.moved_to));
    }
    for (const r of rows) {
      if (!r.insp) {
        const mv = movedTo.get(`${r.listing_id}|${r.date}`);
        if (mv) { r.insp_moved_to = mv; continue; }
        const e = anyQ.get(String(r.listing_id));
        if (e && e !== r.date) r.insp_prior_date = e;
      }
    }
    // The "Today" chip means today — days=1 fetches include tomorrow's checkouts.
    if (this.days === 1) rows = rows.filter(r => r.date === this.asOf());
    const urg = (r: any): number => {
      if (r.turn) return Number(r.turn.urgency) || 0;   // 0 at-risk / 1 same-day / 2 rest
      const u = r.insp ? r.insp.urgency : '';
      return u === 'high' ? 0 : (u === 'med' ? 1 : 2);
    };
    const byDate = new Map<string, any[]>();
    for (const r of rows) {
      if (!byDate.has(r.date)) byDate.set(r.date, []);
      byDate.get(r.date)!.push(r);
    }
    return [...byDate.keys()].sort().map(date => ({
      date,
      rows: byDate.get(date)!.sort((a, b) => (urg(a) - urg(b)) || String(a.name).localeCompare(String(b.name))),
    }));
  }

  private cleanCell(t: any): string {
    if (!t) return `<span class="opb-none">— no clean</span>`;
    const st = CLEAN_DONE.includes(t.status) ? 'done' : (t.status === 'in_progress' ? 'in_progress' : 'unstarted');
    const ids: any[] = (t.assignees && t.assignees.length) ? t.assignees : (t.assigned_to ? [t.assigned_to] : []);
    // R61 (Kai: "when I click on unassigned, it won't let me edit anything")
    // — unassigned is now a live picker, right on the board.
    // R103 #2 (Jesse: "after I assign, I am not able to reassign on this screen")
    // — the picker stays live: the lead cleaner is pre-selected, co-cleaners
    // are listed beside it, and "— unassign —" clears the turn.
    const lead = ids.length ? String(ids[0]) : '';
    const others = ids.slice(1).map(id => this.esc(this.cleanerName(id)));
    // review R103 #15: /cleaners already drops inactive people — keep an archived lead selectable so the row never lies
    const roster: any[] = this.cleaners.filter((x: any) => x.active !== false);
    if (lead && !roster.some((x: any) => String(x.id) === lead)) roster.push({ id: lead, name: `${this.cleanerName(lead)} (archived)` });
    // review R103 #19: a completed clean is pay-of-record — no live picker on it (reassign from the HK tab)
    const who = st === 'done'
      ? `<span class="opb-who" title="Completed — change assignees from the Housekeeping tab">${ids.length ? ids.map(id => this.esc(this.cleanerName(id))).join(' + ') : 'unassigned'}</span>`
      : `<select class="opb-assign ${lead ? 'set' : ''}" data-opb-assignc="${this.esc(t.id)}" data-opb-cur="${this.esc(lead)}" data-opb-others="${this.esc(ids.slice(1).join(','))}" title="${lead ? 'Reassign — pick another cleaner, or unassign' : 'Assign a cleaner'}"><option value="">${lead ? '— unassign —' : '— assign cleaner —'}</option>${roster.map((x: any) => `<option value="${this.esc(x.id)}" ${String(x.id) === lead ? 'selected' : ''}>${this.esc(x.name)}</option>`).join('')}</select>${others.length ? `<span class="opb-who" title="also on this clean">+ ${others.join(' + ')}</span>` : ''}`;
    const badges = [
      t.at_risk ? `<span class="opb-badge risk" title="Same-day checkout and the clean isn't done">⚠ at risk</span>` : '',
      t.same_day ? `<span class="opb-badge sameday" title="Next guest arrives the day of the checkout">⚡ same-day</span>` : '',
      t.eci ? `<span class="opb-badge eci" title="Next guest arrives EARLY">⏰ ECI${t.eci_time ? ` ${this.esc(t.eci_time)}` : ''}</span>` : '',
      t.lco ? `<span class="opb-badge lco" title="Departing guest leaves LATE">🌙 LCO${t.lco_time ? ` ${this.esc(t.lco_time)}` : ''}</span>` : '',
    ].filter(Boolean).join('');
    return `<span class="opb-pill st-${st}">${CLEAN_LABEL[st]}</span>${who}${badges ? `<span class="opb-badges">${badges}</span>` : ''}`;
  }

  private inspCell(q: any, priorDate?: string, row?: any): string {
    if (!q) {
      if (row && row.insp_moved_to) {
        return `<span class="opb-badge moved" title="This inspection was rescheduled away from the clean's date — manage it from Inspections">↪ inspection moved to ${this.esc(this.fmtDay(row.insp_moved_to))}</span>`;
      }
      if (priorDate) {
        return `<span class="opb-none" title="The inspection queue tracks one checkout per property — this listing's inspection row sits on that date">→ queued for ${this.esc(this.fmtDay(priorDate))}</span>`;
      }
      // R81: no reservation checkout mints a queue row here (manual/ad-hoc
      // cleans, or no upcoming checkout in the window) — offer the R64
      // ad-hoc SCHEDULE picker right in the cell instead of a dead label.
      const canSched = row && row.date && this.inspectors.length;
      return `<span class="opb-none" title="No reservation checkout feeds the inspection queue for this property/date (manual cleans don't mint inspection rows)">— no inspection</span>${canSched
        ? `<select class="opb-assign" data-opb-schedi="${this.esc(String(row.listing_id))}|${this.esc(String(row.date))}" title="Schedule an ad-hoc inspection for this date"><option value="">— schedule one —</option>${this.inspectorOptions('')}</select>`
        : ''}`;
    }
    const u = q.urgency === 'high' ? 'high' : (q.urgency === 'med' ? 'med' : 'low');
    const akey = `${q.listing_id}|${q.type === 'checkin_light' ? (q.checkin_date || '') : (q.checkout_date || '')}`;
    const cur = String(q.assigned_inspector || '');
    const who = (this.inspectors.length || cur)
      ? `<select class="opb-assign ${cur ? 'set' : ''}" data-opb-assigni="${this.esc(akey)}" data-opb-cur="${this.esc(cur)}" data-opb-manual="${q.type === 'manual' ? '1' : ''}" title="${cur ? 'Reassign — pick another inspector, or unassign' : 'Assign an inspector'}"><option value="">${cur ? '— unassign —' : '— assign inspector —'}</option>${this.inspectorOptions(cur)}</select>`
      : (cur ? `<span class="opb-who">${this.esc(cur)}</span>` : `<span class="opb-who un">unassigned</span>`);
    const badges = [
      q.missed ? `<span class="opb-badge risk" title="${q.type === 'moved' || q.type === 'manual' ? `Scheduled for ${this.esc(q.checkout_date || '')} and not done — rolled into today` : `Checkout passed with no completed inspection since — rolled into today (checkout ${this.esc(q.checkout_date || '')})`}">⏰ missed</span>` : '',
      // R114: a rescheduled inspection (it moved with its clean, or on its own)
      q.moved_from ? `<span class="opb-badge moved" title="Rescheduled — original date ${this.esc(q.moved_from)}">↪ moved from ${this.esc(this.fmtShortDay(q.moved_from))}</span>` : '',
      q.type === 'checkin_light' ? `<span class="opb-badge light" title="Already inspected — quick pre-arrival check before the guest arrives">🟢 light check-in</span>` : '',
      q.inspected_today ? `<span class="opb-badge ok" title="A completed inspection landed today">✓ inspected today</span>` : '',
      q.walked_early ? `<span class="opb-badge" title="Walked on ${this.esc(q.walked_early)}, before the guest checked out — the inspection after checkout is still owed on ${this.esc(q.post_checkout_due || '')}">👣 walked ${this.esc(this.fmtShortDay(q.walked_early))}</span>` : '',
      q.orphaned ? `<span class="opb-badge risk" title="No stay checks out on ${this.esc(q.origin_date || q.moved_from || '')} any more — the booking was cancelled or its dates changed. Cancel it from Inspections (⋯) if it's not needed.">⚠ booking changed</span>` : '',
      q.skip ? (q.skip.reason === 'cancelled'
        ? `<span class="opb-badge skip" title="Cancelled by the office${q.skip.note ? ` — ${this.esc(q.skip.note)}` : ''}">✕ cancelled</span>`
        : `<span class="opb-badge skip" title="Marked not possible for this checkout">⊘ skipped</span>`) : '',
    ].filter(Boolean).join('');
    return `<span class="opb-pill urg-${u}">${u}</span>${who}${badges ? `<span class="opb-badges">${badges}</span>` : ''}`;
  }

  private draw(): void {
    const body = document.getElementById('opb-body');
    if (!body) return;
    if (!this.board && !this.queue) {
      body.innerHTML = emptyState('Ops board unavailable', 'Both the turnover board and the inspections queue failed to load — check the server log.', '⚠️');
      return;
    }
    const dayGroups = this.joined();
    const all = dayGroups.flatMap(g => g.rows);
    const cleans = all.filter(r => r.turn);
    const cleansDone = cleans.filter(r => CLEAN_DONE.includes(r.turn.status)).length;
    const inspDue = all.filter(r => r.insp && !r.insp.inspected_today && !(r.insp.skip)).length;
    const atRisk = cleans.filter(r => r.turn.at_risk).length;

    // R112: the horizon chips are the shared main.css .chip (active = blue soft).
    const chip = (v: number, label: string) =>
      `<button type="button" class="chip ${this.days === v ? 'active' : ''}" data-opb-days="${v}" aria-pressed="${this.days === v ? 'true' : 'false'}">${label}</button>`;
    const partial = (!this.board || !this.queue)
      ? `<div class="opb-warn">⚠ ${!this.board ? 'The turnover board failed to load — clean cells are blank.' : 'The inspections queue failed to load — inspection cells are blank.'}</div>`
      : '';

    // R115 mobile (#3, Jesse's phone: cramped selects, the right half blank):
    // each cell carries its own kind label (shown only where the column
    // headers are hidden) so a card reads "🧹 Clean [cleaner ▾]" /
    // "🔍 Inspection [inspector ▾]" with full-width selects.
    const rowHtml = (r: any) => `
      <div class="opb-row">
        <div class="opb-prop">${this.esc(r.name || r.listing_id)}</div>
        <div class="opb-cell opb-cell-clean"><span class="opb-lab">🧹 Clean</span>${this.cleanCell(r.turn)}</div>
        <div class="opb-cell opb-cell-insp"><span class="opb-lab">🔍 Inspection</span>${this.inspCell(r.insp, r.insp_prior_date, r)}</div>
        <div class="opb-links">
          <button class="opb-link" data-opb-go="housekeeping" title="Open the Housekeeping tab">HK →</button>
          <button class="opb-link" data-opb-go="inspections" title="Open the Inspections tab">Insp →</button>
        </div>
      </div>`;

    const dayHtml = (g: { date: string; rows: any[] }) => {
      const rel = this.relLabel(g.date);
      return `
        <div class="opb-day">
          <div class="opb-day-head">${this.esc(this.fmtDay(g.date))}${rel ? ` <span class="opb-rel">— ${rel}</span>` : ''}</div>
          <div class="opb-cols"><span>Property</span><span>Clean</span><span>Inspection</span><span></span></div>
          ${g.rows.map(rowHtml).join('')}
        </div>`;
    };

    body.innerHTML = `
      <style>
        /* R112: minimal token restyle — rows on --radius-sm, chips are the
           shared .chip, every colour a :root token (status/urgency palettes
           keep their meaning: blue = in progress / LCO, red = risk, amber = ECI). */
        .opb-head { display:flex; align-items:baseline; gap:12px; margin-bottom:4px; flex-wrap:wrap; }
        .opb-head h2 { margin:0; font-size:18px; font-weight:700; letter-spacing:-0.015em; color:var(--text); }
        .opb-head .sub { font-size:12px; color:var(--text-muted); }
        .opb-basis { font-size:11px; color:var(--text-faint); margin:4px 0 12px; line-height:1.5; }
        .opb-warn { font-size:11px; color:var(--warning-text); background:var(--warning-soft); border:1px solid var(--warning); border-radius:var(--radius-sm); padding:6px 11px; margin-bottom:10px; }
        .opb-top { display:flex; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:14px; }
        .opb-chips { display:flex; gap:6px; flex-wrap:wrap; }
        .opb-sum { font-size:12px; color:var(--text-muted); }
        .opb-sum b { color:var(--text); font-variant-numeric:tabular-nums; }
        .opb-day { margin-bottom:18px; }
        .opb-day-head { font-size:13px; font-weight:700; color:var(--text); margin-bottom:6px; }
        .opb-rel { font-weight:500; color:var(--text-muted); }
        .opb-cols, .opb-row { display:grid; grid-template-columns:minmax(140px, 1.1fr) 1.6fr 1.6fr auto; gap:10px; align-items:center; }
        .opb-cols { font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-faint); padding:0 12px 4px; }
        .opb-row { background:var(--card); border:1px solid var(--border); border-radius:var(--radius-sm); padding:9px 12px; margin-bottom:6px; box-shadow:var(--shadow-card); transition:border-color .15s ease, background .12s ease; }
        .opb-row:hover { border-color:var(--border-strong); }
        .opb-prop { font-size:12.5px; font-weight:600; line-height:1.3; color:var(--text); }
        .opb-cell { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
        .opb-none { font-size:11px; color:var(--text-faint); }
        .opb-pill { display:inline-block; font-size:9px; font-weight:700; padding:2px 7px; border-radius:var(--radius-pill); white-space:nowrap; }
        .opb-pill.st-unstarted { background:var(--bg-subtle); color:var(--text-muted); }
        .opb-pill.st-in_progress { background:var(--accent-soft); color:var(--accent-hover); }
        .opb-pill.st-done { background:var(--success-soft); color:var(--success-text); }
        .opb-pill.urg-high { background:var(--danger-soft); color:var(--danger-text); }
        .opb-pill.urg-med { background:var(--warning-soft); color:var(--warning-text); }
        .opb-pill.urg-low { background:var(--bg-subtle); color:var(--text-muted); }
        .opb-who { font-size:11.5px; color:var(--text); }
        .opb-who.un { color:var(--text-faint); font-style:italic; }
        .opb-badge { font-size:10px; font-weight:600; padding:1px 7px; border-radius:5px; white-space:nowrap; background:var(--bg-subtle); color:var(--text-muted); }
        .opb-badge.risk { background:var(--danger-soft); color:var(--danger-text); }
        .opb-badge.sameday { background:var(--warning-soft); color:var(--warning-text); }
        .opb-badge.eci { background:var(--warning-soft); color:var(--warning); }
        .opb-badge.lco { background:var(--accent-soft); color:var(--accent-hover); }
        .opb-badge.light { background:var(--success-soft); color:var(--success-text); }
        .opb-badge.ok { background:var(--success-soft); color:var(--success-text); }
        .opb-badge.skip { text-decoration:line-through; }
        .opb-badge.moved { background:var(--accent-soft); color:var(--accent); }
        .opb-assign { font-size:11px; padding:4px 6px; border:1px solid var(--border); border-radius:var(--radius-sm); max-width:150px; background:var(--card); color:var(--text); font-family:inherit; }
        .opb-assign:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
        .opb-assign.set { font-weight:600; color:var(--text); background:var(--bg-subtle); }
        .opb-links { display:flex; gap:5px; justify-content:flex-end; }
        .opb-link { font-size:10px; font-weight:600; padding:4px 9px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); color:var(--text-muted); cursor:pointer; white-space:nowrap; transition:border-color .15s ease, color .15s ease; }
        .opb-link:hover { border-color:var(--accent); color:var(--accent); }
        .opb-lab { display:none; }
        .opb-badges { display:contents; }   /* R115: one wrapping line of badges on a phone, unchanged on desktop */
        /* R115 mobile (#3): three lines per card — name + HK→/Insp→, then the
           clean, then the inspection; label + pill in a narrow column, the
           select full width at 16px (no iOS zoom), links ≥ 36px. */
        @media (max-width: 760px) {
          .opb-cols { display:none; }
          .opb-row { grid-template-columns:minmax(0, 1fr) auto; grid-template-areas:"prop links" "clean clean" "insp insp"; gap:8px 10px; padding:10px 12px; }
          .opb-prop { grid-area:prop; font-size:13.5px; align-self:center; overflow-wrap:anywhere; }
          .opb-links { grid-area:links; justify-content:flex-end; align-self:center; }
          .opb-cell-clean { grid-area:clean; }
          .opb-cell-insp { grid-area:insp; }
          .opb-cell { display:grid; grid-template-columns:92px minmax(0, 1fr); column-gap:8px; row-gap:5px; align-items:center; }
          .opb-cell > * { grid-column:1 / -1; justify-self:start; }   /* badges, co-cleaners: full-width lines below */
          .opb-lab { display:block; grid-column:1; grid-row:1; font-size:11px; font-weight:700; color:var(--text-muted); white-space:nowrap; }
          .opb-cell > .opb-lab + .opb-pill { grid-column:1; grid-row:2; }
          .opb-cell > .opb-lab + .opb-pill + .opb-assign, .opb-cell > .opb-lab + .opb-pill + .opb-who { grid-column:2; grid-row:1 / span 2; justify-self:stretch; }
          .opb-cell > .opb-lab + .opb-none, .opb-cell > .opb-lab + .opb-badge { grid-column:2; grid-row:1; }
          .opb-cell > .opb-lab + .opb-none + .opb-assign { grid-column:2; grid-row:2; justify-self:stretch; }
          .opb-cell > .opb-assign { width:100%; max-width:none; min-height:40px; font-size:16px; padding:6px 8px; }
          .opb-cell > .opb-badges { display:flex; flex-wrap:wrap; gap:4px; }
          .opb-link { min-height:36px; padding:8px 12px; font-size:12px; display:inline-flex; align-items:center; }
        }
      </style>
      <div class="opb-head">
        <h2>Ops board</h2>
        <span class="sub">cleans and their inspections, one view per day</span>
      </div>
      <div class="opb-basis">The join of the turnover board and the inspections queue by property and date. Assign or reassign the cleaner / inspector right here; everything else via the HK / Insp links.</div>
      ${partial}
      <div class="opb-top">
        <div class="opb-chips">${chip(1, 'Today')}${chip(3, '3d')}${chip(7, '7d')}</div>
        <span class="opb-sum"><b>${cleans.length}</b> clean${cleans.length === 1 ? '' : 's'} (<b>${cleansDone}</b> done) · <b>${inspDue}</b> inspection${inspDue === 1 ? '' : 's'} due · <b>${atRisk}</b> at-risk</span>
      </div>
      ${dayGroups.length
        ? dayGroups.map(dayHtml).join('')
        : emptyState('Nothing in the window', `No cleans or inspections in the next ${this.days === 1 ? 'day' : `${this.days} days`}.`, '🧹')}`;
    this.bind();
  }

  private bind(): void {
    this.container.querySelectorAll<HTMLElement>('[data-opb-days]').forEach(el => el.addEventListener('click', () => {
      const v = Number(el.dataset.opbDays);
      if (!v || v === this.days) return;
      this.days = v;
      const body = document.getElementById('opb-body');
      if (body) body.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
      void this.load();
    }));
    this.container.querySelectorAll<HTMLElement>('[data-opb-go]').forEach(el => el.addEventListener('click', () => {
      this.goTab(el.dataset.opbGo!);
    }));
    // R61 (Kai): quick-assign straight from the board — cleaner on the clean
    // cell, inspector on the inspection cell.
    this.container.querySelectorAll<HTMLSelectElement>('[data-opb-assignc]').forEach(el => el.addEventListener('change', async () => {
      const turnId = el.dataset.opbAssignc!;
      const cid = el.value;
      const others = (el.dataset.opbOthers || '').split(',').map(x => x.trim()).filter(x => x && x !== cid);
      // R103 #2: empty = unassign everyone (confirm); a pick replaces the LEAD and keeps the co-cleaners.
      if (!cid && !window.confirm('Unassign this clean? (all cleaners come off it)')) { await this.load(); return; }
      el.disabled = true;
      // R107: too many cleans / same-day turns for that cleaner that day
      // 409s with needs_confirm; one confirm overrides (same as inspections).
      const sendC = async (override: boolean): Promise<Response | null> => fetch(`${API}/turns/${encodeURIComponent(turnId)}/assign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cleaner_ids: cid ? [cid, ...others] : [], ...(override ? { override: true } : {}) }),
      }).catch(() => null);
      let r = await sendC(false);
      if (r && r.status === 409) {
        const j = await r.json().catch(() => ({} as any));
        if (j.needs_confirm && window.confirm(j.error || 'Over a cleaning limit — assign anyway?')) r = await sendC(true);
        else { if (!j.needs_confirm) alert(j.error || 'Couldn\'t assign — try again.'); await this.load(); return; }
      }
      if (!r || !r.ok) {
        const j = r ? await r.json().catch(() => ({} as any)) : {};
        alert(j.error || 'Couldn\'t assign — try again.');
        await this.load();   // review #18: the select must show what the server holds, not the failed pick
        return;
      }
      await this.load();
    }));
    this.container.querySelectorAll<HTMLSelectElement>('[data-opb-assigni]').forEach(el => el.addEventListener('change', async () => {
      const key = el.dataset.opbAssigni!;
      const sep = key.lastIndexOf('|');
      const listing_id = key.slice(0, sep);
      const checkout = key.slice(sep + 1);
      const inspector = el.value;
      if (!checkout) return;
      // R103 #2: empty = unassign; on an ad-hoc (manual) inspection that also unschedules it.
      if (!inspector && !window.confirm(el.dataset.opbManual ? 'Unassign — this is an ad-hoc scheduled inspection, so it will be UNSCHEDULED too. Continue?' : 'Unassign this inspection? (A rescheduled one stays on its new date, just with nobody on it.)')) { await this.load(); return; }
      el.disabled = true;
      // R87 #2.2: the 6-days-in-a-row rule 409s with needs_confirm — one
      // confirm overrides.
      const send = async (override: boolean): Promise<Response | null> => fetch(`${API}/inspections/assign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_id, checkout, inspector, ...(override ? { override: true } : {}) }),
      }).catch(() => null);
      let r = await send(false);
      if (r && r.status === 409) {
        const j = await r.json().catch(() => ({} as any));
        if (j.needs_confirm && window.confirm(j.error || 'Over the 6-day streak rule — assign anyway?')) r = await send(true);
        else { if (!j.needs_confirm) alert(j.error || 'Couldn\'t assign — refresh and try again.'); await this.load(); return; }   // review #17: back to what the server holds (not "— unassign —"); R114: a stale row (moved meanwhile) says why
      }
      if (!r || !r.ok) {
        const j = r ? await r.json().catch(() => ({} as any)) : {};
        alert(j.error || 'Couldn\'t assign — try again.');
        await this.load();   // review #18
        return;
      }
      await this.load();
    }));
    // R81: schedule an AD-HOC inspection from a no-inspection cell — the R64
    // manual mechanism (validated today..+60d server-side).
    this.container.querySelectorAll<HTMLSelectElement>('[data-opb-schedi]').forEach(el => el.addEventListener('change', async () => {
      const key = el.dataset.opbSchedi!;
      const sep = key.lastIndexOf('|');
      const listing_id = key.slice(0, sep);
      const checkout = key.slice(sep + 1);
      const inspector = el.value;
      if (!inspector || !checkout) return;
      el.disabled = true;
      const send = async (override: boolean): Promise<Response | null> => fetch(`${API}/inspections/assign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_id, checkout, inspector, manual: true, ...(override ? { override: true } : {}) }),
      }).catch(() => null);
      let r = await send(false);
      if (r && r.status === 409) {
        const j409 = await r.json().catch(() => ({} as any));
        if (j409.needs_confirm && window.confirm(j409.error || 'Over the 6-day streak rule — assign anyway?')) r = await send(true);
        else {
          // R114 review (insp-move #7): a non-confirm 409 (e.g. "This
          // inspection was moved to … — refresh.") says why, then the board
          // reloads to show what the server holds — never a silent reset.
          if (!j409.needs_confirm) alert(j409.error || 'Couldn\'t schedule — refresh and try again.');
          el.disabled = false; el.value = '';
          await this.load();
          return;
        }
      }
      if (!r || !r.ok) {
        el.disabled = false;
        const j = r ? await r.json().catch(() => ({} as any)) : {};
        alert(j.error || 'Couldn\'t schedule — try again.');
        return;
      }
      await this.load();
    }));
  }

  // Jump to a sibling hub tab. Mounted inside the hk-insp HubPage the tab bar
  // is in the DOM — click it (app:navigate no-ops when already on the hub).
  // Fallback: the one-shot hub_tab preselect + app:navigate, the same
  // mechanism App.ts uses for cross-hub deep links.
  private goTab(tab: string): void {
    const btn = document.querySelector<HTMLElement>(`#hub-tabs [data-hub-tab="${tab}"]`);
    if (btn) { btn.click(); return; }
    try { sessionStorage.setItem('hub_tab', tab); } catch { /* private mode */ }
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'hk-insp' }));
  }
}
